import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getEmployerJobs, getJobStats, updateJobStatus, deleteJob } from '../services/jobService';
import { getCompanyProfileByUserId } from '../services/companyService';
import { getEmployerApplications, APPLICATION_STATUS } from '../services/applicationService';
import { requestNotificationPermission } from '../services/fcmService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import JobCard from '../components/JobCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const EmployerDashboard = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    draftJobs: 0,
    closedJobs: 0,
    expiredJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    shortlistedCandidates: 0,
    totalViews: 0,
    avgViewsPerJob: 0,
    responseRate: 0,
    avgTimeToHire: 0
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [recentActivity, setRecentActivity] = useState([]);
  const [topPerformingJobs, setTopPerformingJobs] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);
  const [companyProfileLoading, setCompanyProfileLoading] = useState(true);
  // Fresh employee ID fetched directly from Firestore (not relying on App.js cache)
  const [employeeId, setEmployeeId] = useState(userData?.employeeId || null);

  // Fetch / auto-generate Employee ID fresh from Firestore
  useEffect(() => {
    const fetchEmployeeId = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.employeeId) {
            setEmployeeId(data.employeeId);
          } else {
            // Auto-generate and save if missing
            const shortUid = user.uid.substring(0, 6).toUpperCase();
            const suffix = user.uid.slice(-4).toUpperCase();
            const newId = `EMP-${shortUid}${suffix}`;
            await updateDoc(doc(db, 'users', user.uid), { employeeId: newId });
            setEmployeeId(newId);
          }
        }
      } catch (err) {
        console.error('Error fetching employee ID:', err);
      }
    };
    fetchEmployeeId();
  }, [user?.uid]);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
    if (user?.uid) {
      fetchData();
      fetchCompanyProfile();
      initializeFCM();
    }
  }, [location.state, user?.uid]);

  const initializeFCM = async () => {
    try {
      // Check if FCM token exists, if not request permission and save
      const result = await requestNotificationPermission(user.uid);
      if (result.success) {
        console.log('FCM token initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing FCM:', error);
    }
  };

  const fetchData = async () => {
    console.log('🚀 [EmployerDashboard] fetchData called');
    console.log('🚀 [EmployerDashboard] User UID:', user?.uid);
    console.log('🚀 [EmployerDashboard] User data:', userData);

    if (!user?.uid) {
      console.error('❌ [EmployerDashboard] No user UID available');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔄 [EmployerDashboard] Starting parallel fetch for jobs, stats, and applications');

      // Fetch data sequentially to better handle errors
      const jobsResult = await getEmployerJobs(user.uid, { limit: 10 });
      console.log('📊 [EmployerDashboard] Jobs result received:', jobsResult);

      const statsResult = await getJobStats(user.uid);
      console.log('📊 [EmployerDashboard] Stats result received:', statsResult);

      const applicationsResult = await getEmployerApplications(user.uid);
      console.log('📊 [EmployerDashboard] Applications result received:', applicationsResult);

      // Additional debugging for applications
      if (applicationsResult.success) {
        console.log('📊 [EmployerDashboard] Applications data:', applicationsResult.applications);
        console.log('📊 [EmployerDashboard] Number of applications:', applicationsResult.applications.length);
      } else {
        console.error('❌ [EmployerDashboard] Applications fetch failed:', applicationsResult.error);
      }

      if (jobsResult.success) {
        console.log('✅ [EmployerDashboard] Setting jobs:', jobsResult.jobs);
        setJobs(jobsResult.jobs);

        // Process top performing jobs - always set it
        if (applicationsResult.success) {
          const topJobs = processTopPerformingJobs(jobsResult.jobs, applicationsResult.applications);
          console.log('✅ [EmployerDashboard] Setting top performing jobs:', topJobs);
          setTopPerformingJobs(topJobs);
        } else {
          // If applications failed, still process jobs with empty applications
          const topJobs = processTopPerformingJobs(jobsResult.jobs, []);
          console.log('✅ [EmployerDashboard] Setting top performing jobs (no applications):', topJobs);
          setTopPerformingJobs(topJobs);
        }
      } else {
        console.error('❌ [EmployerDashboard] Failed to fetch jobs:', jobsResult.error);
        setJobs([]);
        setTopPerformingJobs([]);
      }

      // Initialize stats with default values
      let enhancedStats = {
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
        expiredJobs: 0,
        totalApplications: 0,
        newApplications: 0,
        shortlistedCandidates: 0,
        totalViews: 0,
        avgViewsPerJob: 0,
        responseRate: 0,
        avgTimeToHire: 0,
        pendingApplications: 0,
        reviewedApplications: 0,
        rejectedApplications: 0
      };

      if (statsResult.success) {
        console.log('✅ [EmployerDashboard] Setting stats:', statsResult.stats);
        enhancedStats = { ...enhancedStats, ...statsResult.stats };
      } else {
        console.error('❌ [EmployerDashboard] Failed to fetch stats:', statsResult.error);
      }

      if (applicationsResult.success) {
        console.log('✅ [EmployerDashboard] Processing applications:', applicationsResult.applications.length, 'applications');
        const applicationStats = calculateApplicationStats(applicationsResult.applications);
        enhancedStats = {
          ...enhancedStats,
          ...applicationStats
        };

        const activity = generateRecentActivity(applicationsResult.applications, jobsResult.success ? jobsResult.jobs : []);
        setRecentActivity(activity);
      } else {
        console.error('❌ [EmployerDashboard] Failed to fetch applications:', applicationsResult.error);
        setRecentActivity([]);
      }

      console.log('✅ [EmployerDashboard] Final enhanced stats:', enhancedStats);
      setStats(enhancedStats);

    } catch (error) {
      console.error('❌ [EmployerDashboard] Error fetching dashboard data:', error);
      console.error('❌ [EmployerDashboard] Error stack:', error.stack);

      // Set default values on error
      setStats({
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
        expiredJobs: 0,
        totalApplications: 0,
        newApplications: 0,
        shortlistedCandidates: 0,
        totalViews: 0,
        avgViewsPerJob: 0,
        responseRate: 0,
        avgTimeToHire: 0,
        pendingApplications: 0,
        reviewedApplications: 0,
        rejectedApplications: 0
      });
      setJobs([]);
      setRecentActivity([]);
      setTopPerformingJobs([]);
    } finally {
      console.log('🏁 [EmployerDashboard] fetchData completed, setting loading to false');
      setLoading(false);
    }
  };

  const calculateApplicationStats = (applications) => {
    console.log('📊 [EmployerDashboard] Calculating application stats for', applications.length, 'applications');

    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const totalApplications = applications.length;

    // Handle different date formats safely
    const newApplications = applications.filter(app => {
      if (!app.appliedAt) return false;

      let appliedDate;
      if (typeof app.appliedAt === 'number') {
        // Handle timestamp number
        appliedDate = new Date(app.appliedAt);
      } else if (typeof app.appliedAt.toDate === 'function') {
        // Handle Firestore Timestamp
        appliedDate = app.appliedAt.toDate();
      } else if (app.appliedAt instanceof Date) {
        // Handle Date object
        appliedDate = app.appliedAt;
      } else {
        return false;
      }

      return appliedDate >= oneWeekAgo;
    }).length;

    const shortlistedCandidates = applications.filter(app =>
      app.status === APPLICATION_STATUS.SHORTLISTED || app.status === 'selected'
    ).length;

    const pendingApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.PENDING
    ).length;

    const reviewedApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.REVIEWING
    ).length;

    const rejectedApplications = applications.filter(app =>
      app.status === APPLICATION_STATUS.REJECTED
    ).length;

    // Calculate response rate (applications that have been reviewed vs total)
    const responseRate = totalApplications > 0
      ? Math.round(((totalApplications - pendingApplications) / totalApplications) * 100)
      : 0;

    console.log('📊 [EmployerDashboard] Application stats calculated:', {
      totalApplications,
      newApplications,
      shortlistedCandidates,
      pendingApplications,
      reviewedApplications,
      rejectedApplications,
      responseRate
    });

    return {
      totalApplications,
      newApplications,
      shortlistedCandidates,
      pendingApplications,
      reviewedApplications,
      rejectedApplications,
      responseRate
    };
  };

  const generateRecentActivity = (applications, jobs) => {
    console.log('🔄 [EmployerDashboard] Generating recent activity from', applications.length, 'applications and', jobs.length, 'jobs');

    const activities = [];
    const now = new Date();

    // Helper function to safely get date
    const getDate = (dateValue) => {
      if (!dateValue) return null;
      if (typeof dateValue === 'number') return new Date(dateValue);
      if (typeof dateValue.toDate === 'function') return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      return null;
    };

    // Add recent applications
    applications
      .filter(app => app.appliedAt)
      .map(app => ({ ...app, appliedDate: getDate(app.appliedAt) }))
      .filter(app => app.appliedDate)
      .sort((a, b) => b.appliedDate - a.appliedDate)
      .slice(0, 5)
      .forEach(app => {
        const timeDiff = now - app.appliedDate;
        const timeAgo = formatTimeAgo(timeDiff);

        activities.push({
          type: 'application',
          message: `New application from ${app.applicantName} for ${app.jobTitle}`,
          time: timeAgo,
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          timestamp: app.appliedDate
        });
      });

    // Add recent status changes
    applications
      .filter(app => app.updatedAt && app.status !== APPLICATION_STATUS.PENDING)
      .map(app => ({ ...app, updatedDate: getDate(app.updatedAt) }))
      .filter(app => app.updatedDate)
      .sort((a, b) => b.updatedDate - a.updatedDate)
      .slice(0, 3)
      .forEach(app => {
        const timeDiff = now - app.updatedDate;
        const timeAgo = formatTimeAgo(timeDiff);

        let message = '';
        let icon = '';

        switch (app.status) {
          case APPLICATION_STATUS.SHORTLISTED:
            message = `${app.applicantName} shortlisted for ${app.jobTitle}`;
            icon = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
            break;
          case 'selected':
            message = `${app.applicantName} selected for ${app.jobTitle}`;
            icon = 'M5 13l4 4L19 7';
            break;
          case APPLICATION_STATUS.REVIEWING:
            message = `${app.applicantName}'s application reviewed for ${app.jobTitle}`;
            icon = 'M15 12a3 3 0 11-6 0 3 3 0 616 0z';
            break;
          default:
            return;
        }

        activities.push({
          type: 'status_change',
          message,
          time: timeAgo,
          icon,
          timestamp: app.updatedDate
        });
      });

    // Add recent job posts
    jobs
      .filter(job => job.createdAt)
      .map(job => ({ ...job, createdDate: getDate(job.createdAt) }))
      .filter(job => job.createdDate)
      .sort((a, b) => b.createdDate - a.createdDate)
      .slice(0, 2)
      .forEach(job => {
        const timeDiff = now - job.createdDate;
        const timeAgo = formatTimeAgo(timeDiff);

        activities.push({
          type: 'job_post',
          message: `New job posted: ${job.title}`,
          time: timeAgo,
          icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
          timestamp: job.createdDate
        });
      });

    // Sort all activities by timestamp and return top 6
    const sortedActivities = activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 6);

    console.log('✅ [EmployerDashboard] Generated', sortedActivities.length, 'recent activities');
    return sortedActivities;
  };

  const processTopPerformingJobs = (jobs, applications) => {
    console.log('🏆 [EmployerDashboard] Processing top performing jobs from', jobs.length, 'jobs and', applications.length, 'applications');

    const jobPerformance = jobs.map(job => {
      const jobApplications = applications.filter(app => app.jobId === job.id);
      const shortlistedCount = jobApplications.filter(app =>
        app.status === APPLICATION_STATUS.SHORTLISTED || app.status === 'selected'
      ).length;

      return {
        ...job,
        applicationCount: jobApplications.length,
        shortlistedCount,
        viewCount: job.viewCount || 0,
        performanceScore: (jobApplications.length * 2) + (shortlistedCount * 5) + (job.viewCount || 0)
      };
    });

    const topJobs = jobPerformance
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 5);

    console.log('🏆 [EmployerDashboard] Top performing jobs calculated:', topJobs);
    return topJobs;
  };

  const formatTimeAgo = (timeDiff) => {
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
    } else if (hours < 24) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
  };

  const fetchCompanyProfile = async () => {
    console.log('🏢 [EmployerDashboard] Fetching company profile');
    setCompanyProfileLoading(true);
    try {
      const result = await getCompanyProfileByUserId(user.uid);
      console.log('🏢 [EmployerDashboard] Company profile result:', result);

      if (result.success && result.data) {
        setCompanyProfile(result.data);
        setHasCompanyProfile(true);
        console.log('✅ [EmployerDashboard] Company profile found:', result.data);
      } else {
        setHasCompanyProfile(false);
        console.log('ℹ️ [EmployerDashboard] No company profile found');
      }
    } catch (error) {
      console.error('❌ [EmployerDashboard] Error fetching company profile:', error);
      setHasCompanyProfile(false);
    } finally {
      setCompanyProfileLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      const result = await updateJobStatus(jobId, newStatus, user.uid);
      if (result.success) {
        setJobs(jobs.map(job =>
          job.id === jobId ? { ...job, status: newStatus } : job
        ));
        fetchData(); // Refresh stats
      } else {
        alert('Error updating job status: ' + result.error);
      }
    } catch (error) {
      alert('Error updating job status: ' + error.message);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      const result = await deleteJob(jobId, user.uid);
      if (result.success) {
        setJobs(jobs.filter(job => job.id !== jobId));
        fetchData(); // Refresh stats
      } else {
        alert('Error deleting job: ' + result.error);
      }
    } catch (error) {
      alert('Error deleting job: ' + error.message);
    }
  };

  const handlePostJob = () => {
    if (!hasCompanyProfile) {
      if (window.confirm('You need to create a company profile before posting jobs. Would you like to create one now?')) {
        navigate('/company-profile');
      }
      return;
    }
    navigate('/post-job');
  };

  const handleManageCompanyProfile = () => {
    navigate('/company-profile');
  };

  const handleNavigateToJobs = (filter = 'all') => {
    console.log('🔗 [EmployerDashboard] Navigating to manage-jobs with filter:', filter);
    navigate('/manage-jobs', { state: { filter } });
  };

  const handleNavigateToApplications = (filter = 'all') => {
    console.log('🔗 [EmployerDashboard] Navigating to applications with filter:', filter);

    // Map filter values to match APPLICATION_STATUS
    let mappedFilter = filter;
    if (filter === 'shortlisted') {
      mappedFilter = APPLICATION_STATUS.SHORTLISTED;
    } else if (filter === 'selected') {
      mappedFilter = 'selected';
    } else if (filter === 'pending') {
      mappedFilter = APPLICATION_STATUS.PENDING;
    } else if (filter === 'reviewing') {
      mappedFilter = APPLICATION_STATUS.REVIEWING;
    } else if (filter === 'rejected') {
      mappedFilter = APPLICATION_STATUS.REJECTED;
    }

    console.log('🔗 [EmployerDashboard] Mapped filter:', mappedFilter);
    navigate('/applications', { state: { filter: mappedFilter } });
  };

  if (loading) {
    console.log('🔄 [EmployerDashboard] Still loading...');
    return <LoadingSpinner />;
  }

  console.log('🎨 [EmployerDashboard] Rendering with jobs:', jobs);
  console.log('🎨 [EmployerDashboard] Rendering with stats:', stats);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Welcome back, {userData?.firstName}! 👋
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Manage your job postings and find the right candidates.
                </p>
                {/* Employee ID Badge */}
                {employeeId && (
                  <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-xl shadow-md">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                    </svg>
                    <span className="text-sm font-medium">Employee ID:</span>
                    <span className="font-bold font-mono tracking-wider">{employeeId}</span>
                  </div>
                )}
              </div>
              <button
                onClick={handlePostJob}
                className="hidden md:flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Post New Job
              </button>
            </div>

            {/* Company Profile Alert */}
            {!companyProfileLoading && !hasCompanyProfile && (
              <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-amber-900 font-semibold mb-1">Complete Your Company Profile</h3>
                    <p className="text-amber-800 text-sm mb-3">
                      Create your company profile to start posting jobs and attract top talent. A complete profile helps candidates learn more about your organization.
                    </p>
                    <button
                      onClick={handleManageCompanyProfile}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Company Profile
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Company Profile Success Banner */}
            {!companyProfileLoading && hasCompanyProfile && companyProfile && (
              <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start flex-1 min-w-0">
                    {companyProfile.logoUrl && (
                      <img
                        src={companyProfile.logoUrl}
                        alt={companyProfile.companyName}
                        className="w-12 h-12 rounded-lg object-contain bg-white p-1 mr-3 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-blue-900 font-semibold mb-1 truncate">{companyProfile.companyName}</h3>
                      <p className="text-blue-800 text-sm mb-2 truncate">
                        {companyProfile.industry} • {companyProfile.companySize} • {companyProfile.location}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleManageCompanyProfile}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-sm flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Update Profile
                  </button>
                </div>
              </div>
            )}

            {message && (
              <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700 rounded-lg shadow-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {message}
                </div>
              </div>
            )}
          </div>

          {/* Advanced Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Active Jobs */}
            <button
              onClick={() => handleNavigateToJobs('active')}
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium uppercase tracking-wide">Active Jobs</p>
                  <p className="text-4xl font-bold mt-2">{stats.activeJobs}</p>
                  <div className="flex items-center mt-2">
                    <span className="text-emerald-200 text-xs">Currently hiring • Click to view</span>
                  </div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Draft Jobs */}
            <button
              onClick={() => handleNavigateToJobs('draft')}
              className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium uppercase tracking-wide">Draft Jobs</p>
                  <p className="text-4xl font-bold mt-2">{stats.draftJobs}</p>
                  <div className="flex items-center mt-2">
                    <span className="text-orange-200 text-xs">Ready to publish • Click to view</span>
                  </div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Total Applications */}
            <button
              onClick={() => handleNavigateToApplications('all')}
              className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium uppercase tracking-wide">Total Applications</p>
                  <p className="text-4xl font-bold mt-2">{stats.totalApplications || 0}</p>
                  <div className="flex items-center mt-2">
                    <span className="text-blue-200 text-xs">All time • Click to view</span>
                  </div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Shortlisted Candidates */}
            <button
              onClick={() => handleNavigateToApplications('shortlisted')}
              className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium uppercase tracking-wide">Shortlisted</p>
                  <p className="text-4xl font-bold mt-2">{stats.shortlistedCandidates || 0}</p>
                  <div className="flex items-center mt-2">
                    <span className="text-purple-200 text-xs">Top candidates • Click to view</span>
                  </div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>


          {/* Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6 overflow-x-auto scrollbar-none whitespace-nowrap">
                {[
                  { id: 'overview', name: 'Overview', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0a2 2 0 012-2h6l2 2h6a2 2 0 012 2v1' },
                  { id: 'jobs', name: 'Job Management', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
                  { id: 'applications', name: 'Applications', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
                  { id: 'analytics', name: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors duration-200 flex-shrink-0 ${activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                    </svg>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Recent Activity */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Recent Activity</h3>
                    <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">View All</button>
                  </div>
                  <div className="space-y-4">
                    {recentActivity.length > 0 ? (
                      recentActivity.map((activity, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors min-w-0 w-full">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${activity.type === 'application' ? 'bg-blue-100 text-blue-600' :
                              activity.type === 'status_change' ? 'bg-green-100 text-green-600' :
                                activity.type === 'job_post' ? 'bg-orange-100 text-orange-600' :
                                  'bg-purple-100 text-purple-600'
                            }`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={activity.icon} />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{activity.message}</p>
                            <p className="text-gray-500 text-sm">{activity.time}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">
                          <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-sm">No recent activity</p>
                        <p className="text-gray-400 text-xs mt-1">Activity will appear here as you manage applications</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions & Top Jobs */}
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handlePostJob}
                      className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 transform hover:-translate-y-0.5"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="font-semibold">Post New Job</span>
                    </button>
                    <button
                      onClick={handleManageCompanyProfile}
                      className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:from-blue-600 hover:to-cyan-700 transition-all duration-200 transform hover:-translate-y-0.5"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-semibold">{hasCompanyProfile ? 'Manage Company' : 'Create Company'}</span>
                    </button>
                    <Link
                      to="/manage-jobs"
                      className="w-full flex items-center gap-3 p-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold">Manage Jobs</span>
                    </Link>
                    <Link
                      to="/applications"
                      className="w-full flex items-center gap-3 p-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="font-semibold">View Applications</span>
                    </Link>
                  </div>
                </div>

                {/* Top Performing Jobs */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Top Performing Jobs</h3>
                  <div className="space-y-4">
                    {topPerformingJobs.length > 0 ? (
                      topPerformingJobs.slice(0, 3).map((job, index) => (
                        <div key={job.id} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-indigo-600 font-bold text-sm">#{index + 1}</span>
                              <h4 className="font-semibold text-gray-900">{job.title}</h4>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${job.status === 'active' ? 'bg-green-100 text-green-800' :
                                job.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              {job.status}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>{job.applicationCount || 0} applications</span>
                            <span>{job.viewCount || 0} views</span>
                            <span>{job.shortlistedCount || 0} shortlisted</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Score: {job.performanceScore || 0}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">
                          <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-sm">No jobs posted yet</p>
                        <p className="text-gray-400 text-xs mt-1">Post your first job to see performance metrics</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Jobs Tab Content */}
          {activeTab === 'jobs' && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Job Management</h2>
                    <p className="text-sm text-gray-600 mt-1">Manage and track all your job listings</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handlePostJob}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Post New Job
                    </button>
                    <Link
                      to="/manage-jobs"
                      className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-1 px-4 py-2 border border-indigo-200 rounded-xl hover:bg-indigo-50"
                    >
                      View All
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {jobs.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs posted yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">Create your first job posting to start attracting top talent and grow your team.</p>
                    <button
                      onClick={handlePostJob}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Your First Job
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {jobs.map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        showActions={true}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDeleteJob}
                      />
                    ))}
                    {jobs.length >= 10 && (
                      <div className="text-center pt-6 border-t border-gray-200">
                        <Link
                          to="/manage-jobs"
                          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
                        >
                          View All Jobs
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Applications Tab Content */}
          {activeTab === 'applications' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Management</h3>
                <p className="text-gray-600 mb-6">Detailed application management features coming soon!</p>
                <Link
                  to="/applications"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
                >
                  View All Applications
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          )}

          {/* Analytics Tab Content */}
          {activeTab === 'analytics' && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
                <p className="text-gray-600 mb-6">Comprehensive analytics and reporting features coming soon!</p>
                <button className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold">
                  View Analytics
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default EmployerDashboard;
